import { useState, useEffect, useCallback } from "react";
import { useFlowsTemplates, TemplateType, DocumentTemplate } from "../../../../../stores/flowsTemplatesStore";

export interface UseEditTemplateModalProps {
  template: DocumentTemplate | null;
  onClose: () => void;
}

export interface UseEditTemplateModalReturn {
  name: string;
  templateType: TemplateType;
  loading: boolean;
  setName: (name: string) => void;
  setTemplateType: (type: TemplateType) => void;
  handleSubmit: () => Promise<void>;
  handleClose: () => void;
  isSubmitDisabled: boolean;
}

export const TEMPLATE_TYPES: { value: TemplateType; label: string }[] = [
  { value: "monthly_report", label: "Monthly Report" },
  { value: "quarterly_statement", label: "Quarterly Statement" },
  { value: "wire_transfer_summary", label: "Wire Transfer Summary" },
  { value: "account_statement", label: "Account Statement" },
  { value: "client_notes_report", label: "Client Notes Report" },
  { value: "custom_document", label: "Custom Document" },
  { value: "report", label: "Report" },
  { value: "statement", label: "Statement" },
  { value: "confirmation", label: "Confirmation" },
  { value: "tax", label: "Tax Document" },
];

export function useEditTemplateModal({
  template,
  onClose,
}: UseEditTemplateModalProps): UseEditTemplateModalReturn {
  const { updateTemplate } = useFlowsTemplates();
  const [name, setName] = useState("");
  const [templateType, setTemplateType] = useState<TemplateType>("custom_document");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (template) {
      setName(template.name);
      setTemplateType(template.template_type as TemplateType);
    }
  }, [template]);

  const handleSubmit = useCallback(async () => {
    if (!name.trim() || !template) return;

    setLoading(true);
    await updateTemplate(template.identifier, { name: name.trim(), template_type: templateType });
    setLoading(false);
    onClose();
  }, [name, template, templateType, updateTemplate, onClose]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const isSubmitDisabled = !name.trim() || loading;

  return {
    name,
    templateType,
    loading,
    setName,
    setTemplateType,
    handleSubmit,
    handleClose,
    isSubmitDisabled,
  };
}
